import type {ActiveFeedbackForm, SubmittedAnswerRequest} from 'api/v2';

export type {
  ActiveFeedbackForm,
  FeedbackAnswer,
  Indicator,
  MemberIndicatorGroup,
  MemberSummary,
  MyFeedback,
  SubmittedAnswerRequest,
  IndicatorKindOption as SignalKind,
  PaginatedMemberIndicatorGroupList as IndicatorsByMemberPage,
} from 'api/v2';

export const NOTE_MAX_LENGTH = 3000;

export type TrendDirection = 'up' | 'down' | 'flat';

export type ProgramSummary = {id: string; name: string};

export type AdditionalTextField = {
  required: boolean;
  placeholder?: string;
  maxLength?: number;
};

export type FormOption = {
  id: string;
  icon?: string;
  label: string;
  // Stable machine key — form state and submitted answers key off this, never the label.
  value: string;
  additionalTextField?: AdditionalTextField | null;
};

type BaseQuestion = {
  id: string;
  label: string;
  description?: string;
  required?: boolean;
};

export type FeedbackQuestion =
  | (BaseQuestion & {type: 'boolean'; fields: FormOption[]})
  | (BaseQuestion & {type: 'segmented-list'; fields: FormOption[]})
  | (BaseQuestion & {
      type: 'multi-select';
      fields: FormOption[];
      minSelections?: number;
      maxSelections?: number | null;
    })
  | (BaseQuestion & {
      type: 'rating';
      min: number;
      max: number;
      minLabel?: string;
      maxLabel?: string;
    })
  | (BaseQuestion & {
      type: 'text-field';
      placeholder?: string;
      maxLength?: number;
    })
  | (BaseQuestion & {
      type: 'text-area';
      placeholder?: string;
      maxLength?: number;
    });

export type QuestionType = FeedbackQuestion['type'];

export type FormDefinition = {
  id: string;
  title: string;
  questions: FeedbackQuestion[];
};

// `definition` is stored jsonb served verbatim; the API types it as unknown.
export type FeedbackForm = Omit<ActiveFeedbackForm, 'definition'> & {
  definition: FormDefinition;
};

export type FeedbackSubmission = {
  formId: string;
  answers: SubmittedAnswerRequest[];
};

export type FeedbackErrorReason = 'generic' | 'gone';

export type FeedbackFilter = 'all' | 'pending' | 'reviewed';

export type SignalFilters = {
  program: string;
  kinds: string[];
  feedback: FeedbackFilter;
};

// Older stored form drafts carried the question text under `text`/`title`/`prompt`;
// normalize any of these to the `label` the renderer reads.
const QUESTION_LABEL_KEYS = ['label', 'text', 'title', 'prompt'] as const;

export function normalizeFormDefinition(
  definition: FormDefinition,
): FormDefinition {
  return {
    ...definition,
    questions: definition.questions.map((raw) => {
      const question = raw as Record<string, unknown>;
      if (typeof question.label === 'string') return raw;
      for (const key of QUESTION_LABEL_KEYS) {
        if (typeof question[key] === 'string') {
          return {...raw, label: question[key]} as FeedbackQuestion;
        }
      }
      return raw;
    }),
  };
}
